/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Thursday, March 17th 2022, 5:22:31 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import FormControl from "@mui/material/FormControl";
import { useOSAPIFormContext } from "ui-widgets/forms/providers";
import { FormField } from "lib/schemas";
import { CommaSeparatedTagsFormInput, ListButtonAutoComplete, IconText } from "ui-widgets";
import ShortTextIcon from "@mui/icons-material/ShortText";
import ListIcon from "@mui/icons-material/List";
import StorageIcon from "@mui/icons-material/Storage";
import { DatasetReferenceSelectInput } from "datasets/components/DatasetReferenceSelectInput";
import { AjaxSourcePopupInput } from "datasets/components/AjaxSourcePopupInput";
import { ActionDialog } from "ui-widgets/dialogs/GenericDialogs";
import { useToggleState } from "ui-widgets/dialogs/useToggleState";
import { ChoiceOptionGroupComponent } from "./ChoiceOptionGroup";

enum IChoiceDataSource {
  simple = "Simple",
  options = "Options",
  dataset = "Dataset",
}

const IChoiceDataSourceIcons = {
  simple: <ShortTextIcon color="secondary" />,
  options: <ListIcon color="secondary" />,
  dataset: <StorageIcon color="secondary" />,
};

const IChoiceDataSourceDescriptions = {
  simple: (
    <Typography className="os-form-input-help-text" variant="body1">
      <p>Simply type the options for this field, separated by a comma.</p>
    </Typography>
  ),
  options: (
    <Typography className="os-form-input-help-text" variant="body1">
      <p>
        Set a value and label for each option you will want users to choose from. This can allow you to set a
        code/shorthand as a value and a human readable string as a label. This can be very helpful when you may want to
        use such a code to allocate a document number, invoice number or even when configuring advanced conditions for
        your workflow.
      </p>
    </Typography>
  ),
  dataset: (
    <Typography className="os-form-input-help-text" variant="body1">
      <p>Choose or create a reference to one of the datasets available for your organization.</p>
    </Typography>
  ),
};

export const ChoiceFieldSettings = () => {
  const {
    useFormReturn: { watch, setValue },
  } = useOSAPIFormContext<FormField>();

  const watchedOptionGroup = watch<keyof FormField>("option_group");

  const watchedDatasetReference = watch<keyof FormField>("data_set_reference");

  const defaultDataSource: keyof typeof IChoiceDataSource = watchedDatasetReference
    ? "dataset"
    : watchedOptionGroup
    ? "options"
    : "simple";

  const removeDatasetReference = () => {
    setValue("data_set_reference", null);
  };

  const [dataSource, setDataSource] = React.useState<keyof typeof IChoiceDataSource>(defaultDataSource);

  const {
    open: optionDialogOpenState,
    handleClose: closeOptionDialogFn,
    handleOpen: openOptionDialogFn,
  } = useToggleState();

  let choiceOptionsComponent: React.ReactNode;

  switch (dataSource) {
    case "simple":
      choiceOptionsComponent = (
        <CommaSeparatedTagsFormInput<FormField> schema_field_key="choices" label="Enter Choices" />
      );
      break;
    case "options":
      choiceOptionsComponent = (
        <>
          <ChoiceOptionGroupComponent />
        </>
      );
      break;
    case "dataset":
      choiceOptionsComponent = (
        <>
          <AjaxSourcePopupInput<FormField> schema_field_key="ajax_source" />
          <DatasetReferenceSelectInput<FormField> schema_field_key="data_set_reference" />
        </>
      );
      break;
  }

  const onChangeDataSource = (selectedDataSource: keyof typeof IChoiceDataSource) => {
    if (selectedDataSource === "simple" && (watchedOptionGroup || watchedDatasetReference)) {
      openOptionDialogFn();
      return;
    }

    if (selectedDataSource === "options" && watchedDatasetReference) {
      removeDatasetReference();
    }

    if (selectedDataSource === "dataset" && watchedOptionGroup) {
      // We do nothing since dataset reference is has a higher priority
      // than option group so we can still maintain any existing option group
    }

    setDataSource(selectedDataSource);
  };

  return (
    <FormControl>
      <DeleteUseOptionValuesMessage
        optionDialogOpenState={optionDialogOpenState}
        closeOptionDialogFn={closeOptionDialogFn}
      />
      <Stack spacing={2}>
        <ListButtonAutoComplete<keyof typeof IChoiceDataSource>
          options={Object.keys(IChoiceDataSource) as any}
          getOptionLabel={(option) => IChoiceDataSource[option]} // For the TextField
          getPrimaryOptionLabel={(option) => (
            <IconText icon={IChoiceDataSourceIcons[option]} textComponent={IChoiceDataSource[option]} />
          )}
          getSecondaryOptionLabel={null}
          disableClearable
          disableCloseOnBlur
          textFieldProps={{ label: "Data Source" }}
          onChangeCallBack={onChangeDataSource}
          value={dataSource}
        />
        {IChoiceDataSourceDescriptions[dataSource]}
        {choiceOptionsComponent}
      </Stack>
    </FormControl>
  );
};

const DeleteUseOptionValuesMessage = ({ optionDialogOpenState, closeOptionDialogFn }) => {
  return (
    <ActionDialog
      severity="info"
      title="Cannot use basic choices configuration"
      open={optionDialogOpenState}
      onClose={closeOptionDialogFn}
      confirmationAction={closeOptionDialogFn}
      renderActions={() => (
        <DialogActions>
          <Button onClick={closeOptionDialogFn}>Okay</Button>
        </DialogActions>
      )}
      dialogMessage={
        <>
          <p>
            You cannot use simple settings once you have setup advanced options. Instead, change the values in your
            settings to reflect the new options you want or archive this field.
          </p>
        </>
      }
    />
  );
};
